import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, Radar as RadarIcon, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { SectionBand } from "@/components/SectionPrimitives";
import { techLinkFor } from "@/lib/radarLinks";
import { EDITORIAL_PHOTOS } from "@/lib/editorialPhotos";

// -----------------------------------------------------------------------
// Partners index - refonte 2026 (Citwell style).
// Cream hero + photo, then directory grouped by category with wide rows
// that read like a magazine entry.
// -----------------------------------------------------------------------

const CAT_LABELS = {
  technology_partner:      { en: "Technology partners",  fr: "Partenaires technologiques" },
  consulting_partner:      { en: "Consulting partners",  fr: "Partenaires conseil" },
  system_integrator:       { en: "System integrators",   fr: "Intégrateurs" },
  data_digital_specialist: { en: "Data / digital",       fr: "Data / digital" },
  independent_expert:      { en: "Independent experts",  fr: "Experts indépendants" },
  research_academic:       { en: "Research / academic",  fr: "Recherche / académique" },
};

const PartnerRow = ({ p, idx, lang }) => {
  const radar = techLinkFor(p.name);
  const src = p.logo_url || p.photo_url;
  return (
    <article
      data-testid={`partner-row-${idx}`}
      className="grid grid-cols-[80px_1fr] sm:grid-cols-[160px_minmax(0,1fr)_minmax(0,220px)] items-start gap-5 sm:gap-10 py-8 min-h-[100px]"
    >
      <div className="h-12 sm:h-14 flex items-center">
        {src ? (
          <img src={src} alt={p.name} width="160" height="56" className="max-h-10 sm:max-h-12 max-w-full object-contain object-left" />
        ) : (
          <span className="font-display text-3xl text-[#9ca3af]">{p.name?.[0] || "?"}</span>
        )}
      </div>

      <div className="min-w-0 col-span-1">
        <h3 className="font-display text-2xl sm:text-3xl text-[#0B1210] leading-[1.05]">
          {p.name}
        </h3>
        {p.description && (
          <p className="mt-3 text-[14px] sm:text-[15px] text-[#4B5563] leading-relaxed max-w-2xl">
            {p.description}
          </p>
        )}
        {p.expertise?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {p.expertise.slice(0, 6).map((k) => (
              <span key={k} className="text-[11px] font-bold-dl text-[#4B5563] border border-[#E6E0D5] rounded-full px-3 py-1.5">
                {k}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="col-span-2 sm:col-span-1 flex flex-wrap items-center gap-3 sm:flex-col sm:items-start sm:justify-start sm:pt-1">
        <Link
          to={radar.to}
          data-testid={`partner-radar-${idx}`}
          className="group inline-flex items-center gap-1.5 text-[13px] font-bold-dl text-[#0B1210] hover:text-[#00A878] transition-colors min-h-[40px]"
        >
          <RadarIcon size={14} />
          <span className="border-b border-current pb-0.5">{lang === "fr" ? "Signaux Radar" : "Radar signals"}</span>
          <ArrowRight size={13} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
        {p.website && (
          <a
            href={p.website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1.5 text-[13px] font-bold-dl text-[#4B5563] hover:text-[#00A878] min-h-[40px]"
          >
            <Globe size={14} /> {lang === "fr" ? "Site" : "Website"}
          </a>
        )}
      </div>
    </article>
  );
};

const Partners = () => {
  const { lang } = useLang();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/partners?language=${lang}&limit=60`)
      .then((r) => setItems((r.data || []).filter((p) => process.env.REACT_APP_PARTNERS_APPROVED === "true")))
      .finally(() => setLoading(false));
  }, [lang]);

  useEffect(() => {
    if (loading) return;
    const id = location.hash.replace(/^#/, "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, loading]);

  return (
    <div data-testid="partners-page">
      <SeoHead
        lang={lang}
        path="/partners"
        title={lang === "fr" ? "Partenaires - Dataleo" : "Partners - Dataleo"}
        description={lang === "fr"
          ? "Écosystème de partenaires Dataleo : spécialistes technologiques, conseil, intégrateurs, data et experts indépendants."
          : "Dataleo partner ecosystem: technology specialists, consulting, integrators, data and independent experts."}
      />
      <PageHero
        tone="cream"
        kicker={lang === "fr" ? "Écosystème Dataleo" : "Dataleo ecosystem"}
        title={lang === "fr" ? "Nous mobilisons les bonnes expertises." : "We mobilise the right expertise."}
        description={lang === "fr"
          ? "Dataleo pilote l'engagement et mobilise des expertises spécialisées quand c'est nécessaire - technologiques, conseil, intégration, data et experts indépendants."
          : "Dataleo leads the engagement and mobilises specialist capacity where needed - technology, consulting, integration, data and independent experts."}
        primaryCta={{ to: "/contact", label: lang === "fr" ? "Rejoindre l'écosystème" : "Join the ecosystem" }}

        testId="partners-hero"
      />

      <SectionBand tone="cream" testId="partners-directory">
        {loading ? (
          <p className="text-[#9ca3af] text-[13px] font-mono-dl uppercase tracking-widest">
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </p>
        ) : items.length === 0 ? (
          <div className="max-w-2xl bg-white rounded-[24px] border border-[#E6E0D5] p-10">
            <p className="text-[#4B5563] text-[15px] leading-relaxed">
              {lang === "fr"
                ? "Notre écosystème est en cours de publication. Nous travaillons régulièrement avec un réseau resserré de spécialistes."
                : "Our ecosystem is being progressively published. We work regularly with a tight network of specialists."}
            </p>
            <Link to="/contact" className="mt-6 dl-pill dl-pill-primary inline-flex">
              {lang === "fr" ? "Nous solliciter" : "Get in touch"}
              <ArrowRight size={15} strokeWidth={2.5} />
            </Link>
          </div>
        ) : (
          <div className="space-y-20 sm:space-y-24">
            {Object.keys(CAT_LABELS).map((cat) => {
              const bucket = items.filter((p) => (p.category || "technology_partner") === cat);
              if (bucket.length === 0) return null;
              return (
                <section key={cat} id={cat} data-testid={`partners-section-${cat}`} className="scroll-mt-32">
                  <div className="flex items-baseline gap-4 border-b-2 border-[#0B1210] pb-5 mb-2">
                    <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                      {lang === "fr" ? "Catégorie" : "Category"}
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl text-[#0B1210]">
                      {CAT_LABELS[cat]?.[lang] || CAT_LABELS[cat]?.en || cat}
                    </h2>
                    <span className="ml-auto font-mono-dl text-[12px] tracking-widest text-[#4B5563]">
                      {String(bucket.length).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="divide-y divide-[#E6E0D5]">
                    {bucket.map((p, i) => (
                      <PartnerRow key={p.id} p={p} idx={`${cat}-${i}`} lang={lang} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </SectionBand>
    </div>
  );
};

export default Partners;
